﻿import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Pencil } from 'lucide-react';
import { updateLoanSchema, type UpdateLoanInput, type EmployeeLoan, type ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogOverlay } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

interface Props {
  loan: EmployeeLoan;
  open: boolean;
  onClose: () => void;
}

export function EditLoanModal({ loan, open, onClose }: Props) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UpdateLoanInput>({
    resolver: zodResolver(updateLoanSchema),
  });

  useEffect(() => {
    if (loan) {
      reset({
        amount:        parseFloat(loan.amount),
        num_emis:      loan.num_emis,
        emi_amount:    parseFloat(loan.emi_amount),
        interest_rate: parseFloat(loan.interest_rate),
        start_date:    loan.start_date,
        reason:        loan.reason ?? undefined,
      });
    }
  }, [loan, reset]);

  const update = useMutation({
    mutationFn: (data: UpdateLoanInput) => payrollService.updateLoan(loan.id, data),
    onSuccess: () => {
      toast.success('Loan updated');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'loans'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogOverlay />
      <DialogContent className="max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-teal-600" />
            <h2 className="font-semibold text-base">Edit Loan</h2>
          </div>
        </div>

        <div className="mb-3 rounded-md bg-muted px-3 py-2 text-sm font-medium">
          {loan.first_name} {loan.last_name}
          {loan.employee_code && <span className="ml-1 text-muted-foreground">({loan.employee_code})</span>}
        </div>

        <form onSubmit={handleSubmit((d) => update.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="edit_amount">Amount (₹)</Label>
              <Input id="edit_amount" type="number" min="1" {...register('amount')} />
              {errors.amount && <p className="text-xs text-red-500">{errors.amount.message}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit_num_emis">Number of EMIs</Label>
              <Input id="edit_num_emis" type="number" min="1" {...register('num_emis')} />
              {errors.num_emis && <p className="text-xs text-red-500">{errors.num_emis.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="edit_emi_amount">EMI Amount (₹)</Label>
              <Input id="edit_emi_amount" type="number" step="0.01" {...register('emi_amount')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit_interest">Interest Rate (% p.a.)</Label>
              <Input id="edit_interest" type="number" step="0.01" min="0" {...register('interest_rate')} />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit_start_date">Start Date</Label>
            <Controller
              control={control}
              name="start_date"
              render={({ field }) => (
                <DatePicker id="edit_start_date" value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
              )}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit_reason">Reason</Label>
            <Textarea id="edit_reason" rows={2} {...register('reason')} />
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting || update.isPending}>
              {update.isPending ? 'Updating…' : 'Update'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
